import { api } from './api.js';
import { logEditor } from '../state/console.js';
import { useProjectFiles } from '../state/project-files.js';

/** Interchange formats the engine converts to GLB on import. */
export const CONVERTIBLE_SUFFIXES = ['.fbx', '.obj', '.usd', '.usda', '.usdc', '.usdz', '.abc'] as const;

export function isConvertible(path: string): boolean {
  const p = path.toLowerCase();
  return CONVERTIBLE_SUFFIXES.some((s) => p.endsWith(s));
}

/** True when a drag carries files from the operating system. */
export function hasExternalFiles(dt: DataTransfer | null): boolean {
  return Boolean(dt && Array.from(dt.types).includes('Files'));
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** Project-relative destination of a dropped file (keeps a dropped folder's relative path). */
export function importPath(folder: string, file: { name: string; webkitRelativePath?: string }): string {
  const rel = (file.webkitRelativePath || file.name).replace(/\\/g, '/').replace(/^\/+/, '');
  const clean = rel
    .split('/')
    .filter((p) => p && p !== '.' && p !== '..')
    .join('/');
  return folder ? `${folder}/${clean}` : clean;
}

/**
 * Copies files dropped from the OS into `folder` (Unity / Blender style
 * import), then converts FBX / OBJ / USD / Alembic files into GLB models next
 * to them. Sidecars dropped together (MTL libraries, textures) are uploaded
 * first so the converter finds them. Returns the created asset paths.
 */
export async function importExternalFiles(projectId: number, folder: string, files: readonly File[]): Promise<string[]> {
  const created: string[] = [];
  const uploaded: string[] = [];
  for (const file of files) {
    const path = importPath(folder, file);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      await api.put(`/projects/${projectId}/file`, { path, content: toBase64(bytes), encoding: 'base64' });
      uploaded.push(path);
      created.push(path);
    } catch (error) {
      logEditor('error', `Could not import ${file.name}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  for (const path of uploaded.filter(isConvertible)) {
    logEditor('info', `Converting ${path} to a GLB model…`);
    try {
      const result = await api.post<{ glb: string; notes: string[] }>(`/projects/${projectId}/models/convert`, { path });
      for (const note of result.notes) logEditor('warn', `${path}: ${note}`);
      logEditor('info', `Imported ${path} → ${result.glb}. Drag it into the scene, or open it to rig and animate.`);
      created.push(result.glb);
    } catch (error) {
      logEditor('error', `Could not convert ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  useProjectFiles.getState().refreshSoon();
  return created;
}
